import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import bgImage from "../../../images/bg/feature-bg.jpg";

gsap.registerPlugin(ScrollTrigger);

const IconFaceVerify = () => (
  <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="18" cy="14" r="7" stroke="#00c896" strokeWidth="1.8"/>
    <circle cx="15.5" cy="12.5" r="1.5" fill="#00c896"/>
    <circle cx="20.5" cy="12.5" r="1.5" fill="#00c896"/>
    <path d="M15 17c0 0 1 2 3 2s3-2 3-2" stroke="#00c896" strokeWidth="1.4" strokeLinecap="round"/>
    <path d="M7 32c0-6 5-11 11-11s11 5 11 11" stroke="#00c896" strokeWidth="1.8" strokeLinecap="round"/>
    <rect x="4" y="4" width="7" height="2" rx="1" fill="#00c896" opacity="0.5"/>
    <rect x="4" y="4" width="2" height="7" rx="1" fill="#00c896" opacity="0.5"/>
    <rect x="25" y="4" width="7" height="2" rx="1" fill="#00c896" opacity="0.5"/>
    <rect x="30" y="4" width="2" height="7" rx="1" fill="#00c896" opacity="0.5"/>
  </svg>
);

const IconCompliant = () => (
  <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 4L7 9v9c0 7.5 4.8 14.5 11 16.3C24.2 32.5 29 25.5 29 18V9L18 4z"
      stroke="#00c896" strokeWidth="1.8" strokeLinejoin="round" fill="rgba(0,200,150,0.06)"/>
    <path d="M13 18l3.5 3.5 6.5-7" stroke="#00c896" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconAPIIntegrate = () => (
  <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="5" y="9" width="26" height="18" rx="4" stroke="#00c896" strokeWidth="1.8" fill="rgba(0,200,150,0.06)"/>
    <path d="M13 18l-4 3 4 3" stroke="#00c896" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M23 18l4 3-4 3" stroke="#00c896" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M20 14l-4 8" stroke="#00c896" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

const IconDataEnrich = () => (
  <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="18" cy="11" rx="11" ry="5" stroke="#00c896" strokeWidth="1.8" fill="rgba(0,200,150,0.06)"/>
    <path d="M7 11v7c0 2.8 4.9 5 11 5s11-2.2 11-5v-7" stroke="#00c896" strokeWidth="1.8"/>
    <path d="M7 18v7c0 2.8 4.9 5 11 5s11-2.2 11-5v-7" stroke="#00c896" strokeWidth="1.8"/>
  </svg>
);

// ── Section ──────────────────────────────────────────────────────────────────

const FeatureSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    gsap.set(el, { backgroundPosition: "center 0px", backgroundSize: "cover" });
    gsap.to(el, {
      backgroundPositionY: "200px",
      ease: "none",
      scrollTrigger: { trigger: el, start: "top bottom", end: "bottom top", scrub: 1 },
    });
  }, []);

  return (
    <section
      ref={sectionRef}
      className="feature-section parallax-section"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      <div className="container">
        <div className="row align-items-center">
          <div className="col-lg-8">
            <div className="xb-feature-content">
              <div className="sec-title sec-title-center fea-sec-title mb-35">
                <span className="sub-title mb-20" style={{ color: "#00c896" }}>
                  Why TrustEngine?
                </span>
                <h2 className="title title-line_height" style={{ color: "#ffffff" }}>
                  The infrastructure built for identity verification at scale
                </h2>
              </div>

              <div className="row mt-none-30">

                <div className="col-lg-6 col-md-6 mt-30">
                  <div className="xb-feature-item xb-feature-item2">
                    <div className="xb-item--inner xb-border" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
                      <span className="xb-item--icon">
                        <IconFaceVerify />
                      </span>
                      <h2 className="xb-item--title" style={{ color: "#ffffff" }}>
                        Biometric-grade <br /> face verification
                      </h2>
                    </div>
                  </div>
                </div>

                <div className="col-lg-6 col-md-6 mt-30">
                  <div className="xb-feature-item xb-feature-item2 xb-border">
                    <div className="xb-item--inner xb-border" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
                      <span className="xb-item--icon">
                        <IconCompliant />
                      </span>
                      <h2 className="xb-item--title" style={{ color: "#ffffff" }}>
                        OJK & BI-compliant <br /> by design
                      </h2>
                    </div>
                  </div>
                </div>

                <div className="col-lg-6 col-md-6 mt-30">
                  <div className="xb-feature-item xb-feature-item2">
                    <div className="xb-item--inner xb-border" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
                      <span className="xb-item--icon">
                        <IconAPIIntegrate />
                      </span>
                      <h2 className="xb-item--title" style={{ color: "#ffffff" }}>
                        REST API, <br /> integrate in minutes
                      </h2>
                    </div>
                  </div>
                </div>

                <div className="col-lg-6 col-md-6 mt-30">
                  <div className="xb-feature-item xb-feature-item2 xb-border">
                    <div className="xb-item--inner xb-border" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
                      <span className="xb-item--icon">
                        <IconDataEnrich />
                      </span>
                      <h2 className="xb-item--title" style={{ color: "#ffffff" }}>
                        Multi-source data <br /> enrichment
                      </h2>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureSection;
